import React, { useState } from "react";
import { connect } from "react-redux";
import Popup from "../../components/Popup";
import { EntityInput } from "../Entity/EntityInput";
import { SystemEntityType } from "../../methods/SystemEntity";
import { httpClient } from "../../funcs";
import notifyUser, { notifyAboutError } from "../../methods/notifyUser";
import type { InitiatorTrustPayload } from "../Contracts/ContractView/InitiatorTrustBanner";
import "pages/Styles/UIKit_v2.sass";
import "pages/Styles/InitiatorTrustSelfieVerifyPopupV2.sass";

/** Минимальная длина raw base64 для Beorg (как в Register). */
const BEORG_RAW_MIN_LEN = 40;

function beorgRawReady(value: string): boolean {
    return (value || "").trim().length > BEORG_RAW_MIN_LEN;
}

function toastForSelfieStatus(status: string | undefined): { title: string; content: string } {
    switch (status) {
        case "PENDING":
            return {
                title: "Проверка запущена",
                content:
                    "Обычно занимает до нескольких минут. Пришлём уведомление о результате — статус также появится в блоке проверки профиля.",
            };
        case "PASSED":
            return {
                title: "Проверка селфи пройдена",
                content: "Лицо на селфи совпало с фото в паспорте.",
            };
        case "FAILED":
            return {
                title: "Проверка селфи не пройдена",
                content: "Лицо на селфи не совпало с фото в паспорте. Можно повторить проверку.",
            };
        case "ERROR":
            return {
                title: "Не удалось выполнить проверку",
                content: "Попробуйте ещё раз позже или загрузите более чёткие фото.",
            };
        case "NON_RUSSIAN_PASSPORT":
            return {
                title: "Проверка недоступна",
                content: "Для нероссийского паспорта автоматическая проверка селфи не выполняется.",
            };
        default:
            return {
                title: "Проверка завершена",
                content: status ? `Статус: ${status}` : "Обновите страницу, чтобы увидеть результат.",
            };
    }
}

type Props = {
    $store: any;
    $commitToStore: (data: any) => void;
    onClose: () => void;
    onCompleted?: (trust: InitiatorTrustPayload) => void;
};

function InitiatorTrustSelfieVerifyPopup({
    $store,
    $commitToStore,
    onClose,
    onCompleted,
}: Props) {
    const [passportPhoto, setPassportPhoto] = useState("");
    const [selfiePhoto, setSelfiePhoto] = useState("");
    const [passportBeorg, setPassportBeorg] = useState("");
    const [selfieBeorg, setSelfieBeorg] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function handleSubmit() {
        // На API — только raw base64 из getVerificationBase64Raw (без водяного знака).
        if (!beorgRawReady(passportBeorg)) {
            setError("Дождитесь обработки фото паспорта или загрузите снимок заново");
            return;
        }
        if (!beorgRawReady(selfieBeorg)) {
            setError("Дождитесь обработки селфи или сделайте снимок заново");
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const { data } = await httpClient.post("/user/initiator-trust/verify-selfie", {
                passport_photo: passportBeorg.trim(),
                selfie_photo: selfieBeorg.trim(),
            });
            const session = $store.userSession ?? {};
            if (data.self_initiator_trust) {
                $commitToStore({
                    userSession: {
                        ...session,
                        self_initiator_trust: data.self_initiator_trust,
                    },
                });
                if (onCompleted) {
                    onCompleted(data.self_initiator_trust);
                }
            }
            const toast = toastForSelfieStatus(data.selfie_check);
            notifyUser({ $store, $commitToStore }, toast.title, toast.content);
            onClose();
        } catch (e) {
            notifyAboutError({ $store, $commitToStore }, e);
        } finally {
            setSubmitting(false);
        }
    }

    const canSubmit = beorgRawReady(passportBeorg) && beorgRawReady(selfieBeorg) && !submitting;

    return (
        <Popup onClose={onClose} maxWidth="480px">
            <div className="initiator-trust-selfie-popup-v2 ui-kit-v2">
                <h2 className="initiator-trust-selfie-popup-v2__title">
                    Проверка селфи с паспортом
                </h2>

                <div className="initiator-trust-selfie-popup-v2__notice">
                    <p className="initiator-trust-selfie-popup-v2__notice-text">
                        Разовая проверка для подписантов: сравним лицо на селфи с фото в паспорте.
                        Снимки не сохраняются — в профиле останется только результат.
                        Проверка может занять несколько минут — пришлём уведомление.
                    </p>
                </div>

                <p className="initiator-trust-selfie-popup-v2__photos-title">Фотографии</p>
                <p className="initiator-trust-selfie-popup-v2__photos-hint">
                    Сначала разворот паспорта с фото, затем селфи с документом в руках.
                </p>

                <div className="initiator-trust-selfie-popup-v2__field">
                    <div className="initiator-trust-selfie-popup-v2__label">
                        1. Разворот паспорта с фото
                    </div>
                    <div className="initiator-trust-selfie-popup-v2__photo">
                        <EntityInput
                            entity={{
                                keyword: "Паспорт",
                                value: passportPhoto,
                                type: SystemEntityType.Photo,
                                addWatermark: false,
                            }}
                            updateEntity={setPassportPhoto}
                            beorgVerificationPhoto
                            onBeorgVerificationRawBase64={setPassportBeorg}
                        />
                    </div>
                </div>

                <div className="initiator-trust-selfie-popup-v2__field">
                    <div className="initiator-trust-selfie-popup-v2__label">
                        2. Селфи с паспортом
                    </div>
                    <div className="initiator-trust-selfie-popup-v2__photo">
                        <EntityInput
                            entity={{
                                keyword: "Селфи",
                                value: selfiePhoto,
                                type: SystemEntityType.Photo,
                                onlyFromCamera: true,
                                addWatermark: false,
                            }}
                            updateEntity={setSelfiePhoto}
                            beorgVerificationPhoto
                            onBeorgVerificationRawBase64={setSelfieBeorg}
                        />
                    </div>
                </div>

                {error && (
                    <p className="initiator-trust-selfie-popup-v2__error" role="alert">
                        {error}
                    </p>
                )}

                <div className="initiator-trust-selfie-popup-v2__footer">
                    <button
                        type="button"
                        className="new ui-kit-v2-button initiator-trust-selfie-popup-v2__submit action-button primary"
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        <span className="button-text">
                            {submitting ? "Отправляем…" : "Проверить"}
                        </span>
                    </button>
                    <button
                        type="button"
                        className="new ui-kit-v2-button initiator-trust-selfie-popup-v2__cancel action-button secondary"
                        disabled={submitting}
                        onClick={onClose}
                    >
                        <span className="button-text">Отмена</span>
                    </button>
                </div>
            </div>
        </Popup>
    );
}

export default connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: "S" }),
    }),
)(InitiatorTrustSelfieVerifyPopup);
